// DESIGN_DEVIATION: the prototype's organization control is a native <select> styled as an
// input. It cannot show hierarchy or filter ~100 organizations, so this is a hand-rolled tree
// combobox on the palette/picker panel recipe. The panel is `position: fixed`, rendered inline
// (no portal — the tokens live on .sd-root), with a one-time origin probe in case the shadow
// root's container-type makes .sd-root the containing block. Selection happens on mousedown so
// the filter keeps focus and the choice lands before the document mousedown close handler.
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useClickOutside } from '../../hooks/use-click-outside';

import { flattenOrgTree, orgLabel } from './org-tree';
import { Pill, inputStyle, mono, selectStyle } from './ui';

import type { CSSProperties, KeyboardEvent } from 'react';
import type { TenantResponse } from '../../lib/api-client';

export interface OrgTreePickerProps {
  /** Selected tenant id; `''` selects `allOption` when one is given. */
  value: string;
  /** `''` when the `allOption` row is chosen. */
  onChange: (id: string) => void;
  tenants: TenantResponse[];
  /** Per-row badge text — `this org`, `you`, … — or null for none. */
  hint?: (t: TenantResponse) => string | null;
  /** A pseudo-row at the top (id `''`), shown while the query is empty. */
  allOption?: string;
  /** `md` matches `selectStyle`; `sm` is the toolbar variant. */
  size?: 'sm' | 'md';
  /** Test id of the trigger; the panel, filter and rows derive theirs from it. */
  testId: string;
  ariaLabel?: string;
}

interface VisibleRow {
  id: string;
  label: string;
  secondary: string | null;
  level: number;
  isMatch: boolean;
  hint: string | null;
  path: string | null;
}

interface PanelPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  up: boolean;
}

const PANEL_MIN_WIDTH = 320;
const PANEL_MAX_WIDTH = 440;
const PANEL_MAX_HEIGHT = 320;
const MARGIN = 8;
const INDENT = 14;

export function OrgTreePicker({
  value,
  onChange,
  tenants,
  hint,
  allOption,
  size = 'md',
  testId,
  ariaLabel = 'Organization',
}: OrgTreePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<PanelPosition | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const treeId = useId();

  const rows = useMemo(() => flattenOrgTree(tenants, query), [tenants, query]);
  const visible = useMemo<VisibleRow[]>(() => {
    const list: VisibleRow[] = rows.map((r) => {
      const label = orgLabel(r.tenant);
      const name = r.tenant.name;
      return {
        id: r.id,
        label,
        // The human name beside the host-system id, when both exist and differ.
        secondary: r.tenant.external_id && name && name !== label ? name : null,
        level: r.level,
        isMatch: r.isMatch,
        hint: hint ? hint(r.tenant) : null,
        path: r.tenant.path,
      };
    });
    if (allOption && query.trim() === '') {
      list.unshift({ id: '', label: allOption, secondary: null, level: 0, isMatch: true, hint: null, path: null });
    }
    return list;
  }, [rows, hint, allOption, query]);
  const safeHighlight = Math.min(highlight, Math.max(0, visible.length - 1));

  const selected = tenants.find((t) => t.id === value);
  // Tenants arrive after the first render, so the raw id is the honest fallback.
  const triggerLabel = value === '' && allOption ? allOption : selected ? orgLabel(selected) : value || '—';

  const close = useCallback(() => {
    setOpen(false);
    setPos(null);
  }, []);

  const openPanel = useCallback(() => {
    // Start on the current value, so Enter re-confirms it.
    const base = flattenOrgTree(tenants, '');
    const offset = allOption ? 1 : 0;
    const idx = value === '' && allOption ? 0 : base.findIndex((r) => r.id === value);
    setHighlight(idx >= 0 ? (value === '' ? 0 : idx + offset) : 0);
    setQuery('');
    setPos(null);
    setOpen(true);
  }, [tenants, allOption, value]);

  useClickOutside(wrapRef, close, open);

  const select = useCallback(
    (id: string) => {
      onChange(id);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close],
  );

  // Measure once the panel exists. `pos === null` renders it hidden at (0,0), so its own
  // rect reveals where fixed (0,0) actually lands — the viewport, or `.sd-root` when the
  // shadow root's container-type made it the containing block — and the offset is subtracted.
  useLayoutEffect(() => {
    if (!open || pos !== null) return;
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const r = trigger.getBoundingClientRect();
    const origin = panel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom - MARGIN;
    const spaceAbove = r.top - MARGIN;
    const up = spaceBelow < 200 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(PANEL_MAX_HEIGHT, up ? spaceAbove : spaceBelow));
    const width = Math.max(
      PANEL_MIN_WIDTH,
      Math.min(PANEL_MAX_WIDTH, Math.max(r.width, PANEL_MIN_WIDTH), vw - 2 * MARGIN),
    );
    const left = Math.min(Math.max(r.left, MARGIN), Math.max(MARGIN, vw - width - MARGIN));
    const top = up ? r.top - 4 : r.bottom + 4;
    setPos({ top: top - origin.top, left: left - origin.left, width, maxHeight, up });
  }, [open, pos]);

  // Focus the filter once the panel is actually visible. `autoFocus` alone does
  // not do it in a real browser: the first frame renders `visibility: hidden`
  // for the origin probe, and a hidden element ignores `focus()`. (jsdom does
  // not model that, which is why only the browser pass caught it.)
  useLayoutEffect(() => {
    if (open && pos !== null) inputRef.current?.focus();
  }, [open, pos !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measure when the page moves under the panel; the panel's own list scrolling is not that.
  useEffect(() => {
    if (!open) return;
    const reposition = (e?: Event) => {
      if (e && e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      setPos(null);
    };
    window.addEventListener('resize', reposition);
    document.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      document.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  useEffect(() => {
    // Optional call: jsdom has no scrollIntoView.
    highlightRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [safeHighlight, open]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    // Enter and Space reach onClick through the button's own activation.
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      e.stopPropagation();
      openPanel();
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  const onFilterKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setHighlight((h) => Math.min(h + 1, Math.max(0, visible.length - 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setHighlight((h) => Math.max(h - 1, 0));
        break;
      case 'Enter': {
        e.preventDefault();
        e.stopPropagation();
        const row = visible[safeHighlight];
        if (row) select(row.id);
        break;
      }
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
        break;
      case 'Tab':
        // Let focus move on naturally; just do not leave a panel behind.
        close();
        break;
      default:
        break;
    }
  };

  const triggerStyle: CSSProperties = {
    ...selectStyle,
    ...(size === 'sm' ? { padding: '3px 6px', fontSize: 11 } : {}),
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: 320,
    textAlign: 'left',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    top: pos?.top ?? 0,
    left: pos?.left ?? 0,
    width: pos?.width ?? PANEL_MIN_WIDTH,
    maxHeight: pos?.maxHeight ?? PANEL_MAX_HEIGHT,
    ...(pos?.up ? { transform: 'translateY(-100%)' } : {}),
    visibility: pos ? 'visible' : 'hidden',
    zIndex: 60,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--surface-raise)',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    boxShadow: 'var(--shadow-soft)',
    overflow: 'hidden',
  };

  return (
    <span ref={wrapRef} style={{ display: 'inline-flex', position: 'relative' }}>
      <button
        type="button"
        ref={triggerRef}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="tree"
        aria-controls={treeId}
        aria-label={ariaLabel}
        data-value={value}
        data-testid={testId}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onTriggerKeyDown}
        style={triggerStyle}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {triggerLabel}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          style={{
            flexShrink: 0,
            color: 'var(--text-soft)',
            ...(open ? { transform: 'rotate(180deg)' } : {}),
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div ref={panelRef} data-testid={`${testId}-panel`} style={panelStyle}>
          <input
            ref={inputRef}
            autoFocus
            value={query}
            placeholder="filter organizations…"
            aria-autocomplete="list"
            aria-controls={treeId}
            data-testid={`${testId}-filter`}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onFilterKeyDown}
            style={{
              ...inputStyle,
              border: 0,
              borderBottom: '1px solid var(--border)',
              borderRadius: 0,
              background: 'var(--bg-alt)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          <div
            role="tree"
            id={treeId}
            aria-label={ariaLabel}
            style={{ overflowY: 'auto', padding: 4, flex: 1, minHeight: 0 }}
          >
            {visible.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
                No organizations match
              </div>
            )}
            {visible.map((row, i) => {
              const isHighlighted = i === safeHighlight;
              const isSelected = row.id === value;
              return (
                <div
                  key={row.id || '__all'}
                  ref={isHighlighted ? highlightRef : undefined}
                  role="treeitem"
                  aria-level={row.level + 1}
                  aria-selected={isSelected}
                  data-id={row.id}
                  data-match={row.isMatch ? 'true' : 'false'}
                  data-testid={`${testId}-option`}
                  {...(row.path ? { title: row.path } : {})}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    // Mousedown, not click: the filter keeps focus and the choice lands
                    // before the document mousedown handler closes the panel.
                    e.preventDefault();
                    select(row.id);
                  }}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: isHighlighted ? 'var(--accent-soft)' : 'transparent',
                    opacity: row.isMatch ? 1 : 0.55,
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {isHighlighted && (
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: 2,
                        top: 6,
                        bottom: 6,
                        width: 2,
                        borderRadius: 2,
                        background: 'var(--accent)',
                      }}
                    />
                  )}
                  {Array.from({ length: row.level }, (_, k) => (
                    <span
                      key={k}
                      aria-hidden
                      style={{
                        width: INDENT,
                        alignSelf: 'stretch',
                        flexShrink: 0,
                        borderLeft: '1px solid var(--border)',
                        marginLeft: 6,
                      }}
                    />
                  ))}
                  <span style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {row.label}
                  </span>
                  {row.secondary && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontFamily: mono,
                        color: 'var(--text-mute)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {row.secondary}
                    </span>
                  )}
                  {row.hint && (
                    <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
                      <Pill>{row.hint}</Pill>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </span>
  );
}
